import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from 'react';
import {
  fetchArticles,
  fetchVideos,
  fetchWriters,
  fetchSettings,
  fetchLinks,
  fetchSections,
  fetchHeros,
  fetchCategories,
} from '../api';
import { SAMPLE_ARTICLES, VIDEO_DATA, MAIN_NAV, WRITERS as STATIC_WRITERS } from '../constants';
import { Article, VideoItem } from '../types';

interface DataContextType {
  articles: Article[];
  videos: VideoItem[];
  writers: any[];
  settings: any;
  headerLinks: any[];
  footerLinks: any[];
  sections: any[];
  heros: any[];
  categories: any[];
  isLoading: boolean;
  apiOnline: boolean;
  refresh: () => void;
}

const DataContext = createContext<DataContextType | undefined>(undefined);

export function DataProvider({ children }: { children: React.ReactNode }) {
  const [articles, setArticles] = useState<Article[]>(SAMPLE_ARTICLES as Article[]);
  const [videos, setVideos] = useState<VideoItem[]>(VIDEO_DATA as VideoItem[]);
  const [writers, setWriters] = useState<any[]>(STATIC_WRITERS);
  const [settings, setSettings] = useState<any>({});
  const [headerLinks, setHeaderLinks] = useState<any[]>([]);
  const [footerLinks, setFooterLinks] = useState<any[]>([]);
  const [sections, setSections] = useState<any[]>([]);
  const [heros, setHeros] = useState<any[]>([]);
  const [categories, setCategories] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [apiOnline, setApiOnline] = useState(false);

  const load = useCallback(async () => {
    setIsLoading(true);
    try {
      const [
        arts,
        vids,
        wrs,
        sets,
        hLinks,
        fLinks,
        sects,
        heroSlides,
        cats,
      ] = await Promise.allSettled([
        fetchArticles(),
        fetchVideos(),
        fetchWriters(),
        fetchSettings(),
        fetchLinks('header'),
        fetchLinks('footer'),
        fetchSections(),
        fetchHeros(),
        fetchCategories(),
      ]);

      setApiOnline(true);

      if (arts.status === 'fulfilled' && Array.isArray(arts.value) && arts.value.length > 0) {
        setArticles(arts.value);
      }
      if (vids.status === 'fulfilled' && Array.isArray(vids.value) && vids.value.length > 0) {
        setVideos(vids.value);
      }
      if (wrs.status === 'fulfilled' && Array.isArray(wrs.value) && wrs.value.length > 0) {
        setWriters(wrs.value);
      }
      if (sets.status === 'fulfilled' && sets.value) {
        setSettings(sets.value);
        // Apply dynamic theme color from settings
        if (sets.value.primary_color) {
          document.documentElement.style.setProperty('--color-primary', sets.value.primary_color);
        }
        if (sets.value.secondary_color) {
          document.documentElement.style.setProperty('--color-secondary', sets.value.secondary_color);
        }

        // Apply dynamic SEO and Analytics settings
        if (sets.value.seo_settings) {
          const seo = typeof sets.value.seo_settings === 'string' 
            ? JSON.parse(sets.value.seo_settings) 
            : sets.value.seo_settings;

          if (seo.meta_description) {
            let descTag = document.querySelector('meta[name="description"]');
            if (!descTag) {
              descTag = document.createElement('meta');
              descTag.setAttribute('name', 'description');
              document.head.appendChild(descTag);
            }
            descTag.setAttribute('content', seo.meta_description);
          }

          if (seo.meta_keywords) {
            let keysTag = document.querySelector('meta[name="keywords"]');
            if (!keysTag) {
              keysTag = document.createElement('meta');
              keysTag.setAttribute('name', 'keywords');
              document.head.appendChild(keysTag);
            }
            keysTag.setAttribute('content', seo.meta_keywords);
          }

          if (seo.google_analytics_id) {
            if (!document.getElementById('google-analytics')) {
              const script = document.createElement('script');
              script.id = 'google-analytics';
              script.async = true;
              script.src = `https://www.googletagmanager.com/gtag/js?id=${seo.google_analytics_id}`;
              document.head.appendChild(script);

              const inlineScript = document.createElement('script');
              inlineScript.id = 'google-analytics-inline';
              inlineScript.innerHTML = `
                window.dataLayer = window.dataLayer || [];
                function gtag(){dataLayer.push(arguments);}
                gtag('js', new Date());
                gtag('config', '${seo.google_analytics_id}');
              `;
              document.head.appendChild(inlineScript);
            }
          }
        }

        // Apply appearance settings (dynamic fonts and layout)
        if (sets.value.appearance_settings) {
          const app = typeof sets.value.appearance_settings === 'string'
            ? JSON.parse(sets.value.appearance_settings)
            : sets.value.appearance_settings;

          if (app.font_family) {
            const fontId = 'dynamic-font';
            let link = document.getElementById(fontId) as HTMLLinkElement;
            if (!link) {
              link = document.createElement('link');
              link.id = fontId;
              link.rel = 'stylesheet';
              document.head.appendChild(link);
            }
            link.href = `https://fonts.googleapis.com/css2?family=${app.font_family.replace(/['\"]/g, '')}:wght@300;400;500;700;900&display=swap`;
            document.body.style.fontFamily = `"${app.font_family}", sans-serif`;
          }

          if (app.layout_type === 'boxed') {
            document.body.classList.add('max-w-7xl', 'mx-auto', 'shadow-2xl', 'bg-slate-100');
          } else {
            document.body.classList.remove('max-w-7xl', 'mx-auto', 'shadow-2xl', 'bg-slate-100');
          }
        }
      }
      if (hLinks.status === 'fulfilled' && Array.isArray(hLinks.value) && hLinks.value.length > 0) {
        setHeaderLinks(hLinks.value);
      }
      if (fLinks.status === 'fulfilled' && Array.isArray(fLinks.value) && fLinks.value.length > 0) {
        setFooterLinks(fLinks.value);
      }
      if (sects.status === 'fulfilled' && Array.isArray(sects.value)) {
        setSections(sects.value);
      }
      if (heroSlides.status === 'fulfilled' && Array.isArray(heroSlides.value)) {
        setHeros(heroSlides.value);
      }
      if (cats.status === 'fulfilled' && Array.isArray(cats.value)) {
        setCategories(cats.value);
      }
    } catch {
      setApiOnline(false);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <DataContext.Provider
      value={{
        articles,
        videos,
        writers,
        settings,
        headerLinks,
        footerLinks,
        sections,
        heros,
        categories,
        isLoading,
        apiOnline,
        refresh: load,
      }}
    >
      {children}
    </DataContext.Provider>
  );
}

export function useData() {
  const ctx = useContext(DataContext);
  if (!ctx) throw new Error('useData must be used inside DataProvider');
  return ctx;
}
